import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import moment from "moment";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import DataTableSkeleton from "../../components/DataTableSkeleton";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import {
  approveAdminWithdrawal,
  formatCurrency,
  getAdminWithdrawals,
  getWithdrawalStatusBadge,
  initiateCashfreePayout,
  normalizePagination,
  normalizeWithdrawalList,
  rejectAdminWithdrawal,
  WITHDRAWAL_STATUSES,
} from "../../helper/affiliate_helper";
import { dataTableCustomStyles, dataTablePaginationOptions } from "../../components/admin/dataTableConfig";
import AdminRowActions, { AdminActionButton } from '../../components/admin/AdminRowActions';

const getWithdrawalId = (row) => row?.id || row?._id || null;

export default function AffiliateWithdrawals() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [status, setStatus] = useState("requested");
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (status) params.status = status;
      const res = await getAdminWithdrawals(params);
      if (res?.success === false) {
        toast.error(res?.message || "Failed to load withdrawals");
        setData([]);
        return;
      }
      const rows = normalizeWithdrawalList(res);
      setData(rows);
      const pagination = normalizePagination(res);
      if (pagination) {
        setTotalPages(pagination.pages);
        setTotalRecords(pagination.total);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Failed to load withdrawals");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, [page, status]);

  const handleApprove = async (row) => {
    const id = getWithdrawalId(row);
    if (!id) return;
    setActionLoadingId(id);
    try {
      const res = await approveAdminWithdrawal(id);
      if (res?.success === false) {
        toast.error(res?.message || "Approve failed");
        return;
      }
      toast.success(res?.message || "Withdrawal approved");
      fetchRows();
    } catch (error) {
      toast.error(error?.message || "Approve failed");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCashfree = async (row) => {
    const id = getWithdrawalId(row);
    if (!id) return;
    setActionLoadingId(`cf-${id}`);
    try {
      const res = await initiateCashfreePayout(id);
      if (res?.success === false) {
        toast.error(res?.message || "Cashfree transfer failed");
        return;
      }
      toast.success(res?.message || "Cashfree transfer initiated");
      fetchRows();
    } catch (error) {
      toast.error(error?.message || "Cashfree transfer failed");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    const id = getWithdrawalId(rejectTarget);
    if (!id || !rejectReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    setActionLoadingId(id);
    try {
      const res = await rejectAdminWithdrawal(id, rejectReason.trim());
      if (res?.success === false) {
        toast.error(res?.message || "Reject failed");
        return;
      }
      toast.success(res?.message || "Withdrawal rejected");
      setRejectTarget(null);
      setRejectReason("");
      fetchRows();
    } catch (error) {
      toast.error(error?.message || "Reject failed");
    } finally {
      setActionLoadingId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "Affiliate",
        selector: (row) => row.affiliateId,
        sortable: true,
        grow: 1.2,
        cell: (row) => (
          <div className="py-1">
            <code className="small">{String(row.affiliateId || "—").slice(-8)}</code>
          </div>
        ),
      },
      {
        name: "Amount",
        selector: (row) => row.requestedAmount,
        sortable: true,
        cell: (row) => formatCurrency(row.requestedAmount, row.currency),
      },
      {
        name: "Method",
        selector: (row) => row.payoutMethod,
        cell: (row) => row.payoutMethod || "—",
      },
      {
        name: "Status",
        selector: (row) => row.status,
        cell: (row) => (
          <Badge bg={getWithdrawalStatusBadge(row.status)}>{row.status}</Badge>
        ),
      },
      {
        name: "Requested",
        selector: (row) => row.createdAt,
        cell: (row) =>
          row.createdAt ? moment(row.createdAt).format("DD MMM YYYY, hh:mm A") : "—",
      },
      {
        name: "Actions",
        cell: (row) => {
          const id = getWithdrawalId(row);
          const busy = actionLoadingId === id || actionLoadingId === `cf-${id}`;
          return (
            <AdminRowActions className="flex-wrap">
              {row.status === "requested" && (
                <>
                  <AdminActionButton icon="ri-check-line" label="Approve" tone="success" disabled={busy} onClick={() => handleApprove(row)}>
                    Approve
                  </AdminActionButton>
                  <AdminActionButton icon="ri-close-line" label="Reject" tone="danger" disabled={busy} onClick={() => setRejectTarget(row)}>
                    Reject
                  </AdminActionButton>
                </>
              )}
              {row.status === "approved" && (
                <Button size="sm" variant="primary" disabled={busy} onClick={() => handleCashfree(row)}>
                  Pay via Cashfree
                </Button>
              )}
              <AdminActionButton
                to={`/affiliate-payouts/new?affiliateId=${row.affiliateId}`}
                icon="ri-bank-card-line"
                label="Manual payout"
              >
                Manual payout
              </AdminActionButton>
            </AdminRowActions>
          );
        },
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        minWidth: "220px",
      },
    ],
    [actionLoadingId]
  );

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-md-flex align-items-center justify-content-between gap-3 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Affiliate Withdrawals</li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">Affiliate Withdrawals</h4>
            <p className="text-secondary small mb-0 mt-1">
              Review withdrawal requests, approve, then pay via Cashfree or a manual payout.
            </p>
          </div>
          <Link to="/affiliate-commissions" className="btn btn-outline-primary btn-sm mt-3 mt-md-0">
            Commissions queue
          </Link>
        </div>

        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Form.Label className="small text-muted">Status</Form.Label>
                <Form.Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                  <option value="">All</option>
                  {WITHDRAWAL_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            {loading ? (
              <DataTableSkeleton />
            ) : data.length === 0 ? (
              <AdminEmptyState title="No withdrawals" description="No withdrawal requests match your filters." />
            ) : (
              <DataTable
                columns={columns}
                data={data}
                customStyles={dataTableCustomStyles}
                pagination
                paginationServer
                paginationTotalRows={totalRecords}
                paginationDefaultPage={page}
                onChangePage={setPage}
                paginationPerPage={limit}
                paginationComponentOptions={dataTablePaginationOptions}
              />
            )}
          </Card.Body>
        </Card>
        <Footer />
      </div>

      <Modal show={!!rejectTarget} onHide={() => setRejectTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reject withdrawal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why is this withdrawal being rejected?"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleReject} disabled={!rejectReason.trim()}>Reject</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
